import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import API from "../Api/Api";
import {
  annualSpaPotentialOptions,
  poolGroupOptions,
} from "../Components/Prospects/ProspectTable/data";
import { prospectStatusOptions } from "../Components/Prospects/ProspectFilters";
import { API_END_POINT } from "../Api/EndPoints";
import moment from "moment";

export const useProspectData = () => {
  // for now country is fetched on another component when called
  const pageSize = 10;
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { countries } = useSelector((state) => state.countryReducer);
  const { user } = useSelector((state) => state.userReducer);
  const { prospects, prospectsLoading } = useSelector(
    (state) => state.prospectsReducer,
  );
  const [filters, setFilters] = useState({});
  const [pageCount, setPageCount] = useState(null);
  const [selectOptions, setSelectOptions] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchAbortRef = useRef(null);
  const spaMarquesAbortRef = useRef(null);

  const fetchData = async (page = 1, search, filters) => {
    fetchAbortRef.current?.abort();
    fetchAbortRef.current = new AbortController();
    const signal = fetchAbortRef.current.signal;
    dispatch({ type: "PROSPECTS_LOADING" });
    try {
      const {
        status,
        followUpDate,
        area,
        country,
        annualSpaPotential,
        spaMarques,
        poolSpaGroup,
      } = filters;

      const marque = user?.attributes?.marque?.data?.id;
      const query = {
        sort: "createdAt:desc",
        fields: [
          "id",
          "companyName",
          "status",
          "followUpDate",
          "area",
          "annualSpaPotential",
        ],
        populate: {
          actions: {
            fields: ["id", "type", "date", "isReminder", "comment"],
            populate: {
              interlocutor: {
                fields: ["id", "firstName", "lastName", "occupation"],
              },
            },
            sort: ["createdAt:desc"],
          },
          geoEntity: {
            populate: true,
          },
          note: {
            fields: ["id", "globalNote", "comment"],
          },
          business_affiliate: {
            populate: {
              user: {
                fields: ["id", "firstName", "lastName"],
              },
            },
          },
        },
      };

      const config = {
        params: {
          ...query,
          filters: {
            companyName: { $containsi: search },
            ...(user?.attributes?.userRole === 2 && {
              business_affiliate: {
                id: {
                  $eq: user?.attributes?.business_affiliate?.data?.id,
                },
              },
            }),
            marque: { id: { $eq: marque } },
            ...(status && { status: { $eq: status?.code } }),
            ...(followUpDate && {
              followUpDate: {
                $gte: moment(followUpDate).utc(true).startOf("day").toDate(),
                $lte: moment(followUpDate).utc(true).endOf("day").toDate(),
              },
            }),
            ...(area && { area: { $eq: area?.code } }),
            ...(country && {
              deliveryAddresses: {
                country: { id: { $eq: country?.code } },
              },
            }),
            ...(annualSpaPotential && {
              annualSpaPotential: annualSpaPotential?.code
                ?.split("-")
                .reduce((acc, val, index) => {
                  if (index === 0) {
                    acc.$gte = Number(val);
                  } else if (index === 1) {
                    acc.$lte = Number(val);
                  }
                  return acc;
                }, {}),
            }),
            ...(spaMarques?.length > 0 && {
              spaMarques: { id: { $in: spaMarques } },
            }),
            ...(poolSpaGroup && { poolSpaGroup: { $eq: poolSpaGroup?.code } }),
          },
          pagination: {
            page,
            pageSize,
          },
        },
      };

      config.signal = signal;
      const { data } = await API.get(`api/prospects`, config);

      if (page === 1) {
        dispatch({ type: "PROSPECTS_SUCCESS", payload: data?.data });
      } else {
        dispatch({
          type: "PROSPECTS_SUCCESS",
          payload: [...prospects, ...data?.data],
        });
      }
      setPageCount(data?.meta?.pagination?.pageCount);
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return dispatch({ type: "PROSPECTS_CANCELLED" });
      dispatch({ type: "PROSPECTS_ERROR" });
      console.log(error);
    }
  };

  useEffect(() => {
    if (loading) return;
    spaMarquesAbortRef.current?.abort();
    spaMarquesAbortRef.current = new AbortController();
    const signal = spaMarquesAbortRef.current.signal;
    (async function getData() {
      setLoading(true);
      try {
        const { data: spaMarqueData } = await API.get(
          `${API_END_POINT}api/spa-marques`,
          { signal },
        );

        setSelectOptions((prev) => ({
          ...prev,
          status: prospectStatusOptions(t),
          area: [],
          annualSpaPotential: annualSpaPotentialOptions,
          spaMarques: spaMarqueData?.data?.map((marque) => ({
            label: marque.attributes?.name,
            value: marque?.id,
          })),
          poolSpaGroup: poolGroupOptions(t),
        }));
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      spaMarquesAbortRef.current?.abort();
      fetchAbortRef.current?.abort();
    };
  }, [t]);

  useEffect(() => {
    setSelectOptions((prev) => ({
      ...prev,
      country: countries?.map((option) => ({
        name: option.name,
        code: option.code,
      })),
    }));
  }, [countries]);

  return {
    fetchData,
    prospects,
    loading: prospectsLoading,
    pageCount,
    setFilters,
    filters,
    selectOptions,
  };
};
